class Wishlist {
  constructor() {
    this.isWishlistPage = false
    this.storageKey = 'm-wishlist-products'
    this.products = []
    this.productNodes = {}

    this.pageTemplate = 'page.wishlist'
    this.addedClass = 'added-to-wishlist'
    this.hasItemClass = 'wishlist-has-item'
    this.selectors = {
      container: '.m-wishlist-page-content__wrapper',
      noProducts: '.m-wishlist-no-products',
      wrapper: '.m-wishlist-card',
      productCard: '.m-product-card',
      wishlistButton: '.m-wishlist-button',
      wishlistText: '.m-wishlist-button-text',
      removeButton: '.m-wishlist-remove-button',
      count: '.m-wishlist-count',
      unavailableNotice: '.m-wishlist-unavailable-notice'
    }
    this.products = Array.from(new Set(Array.from(JSON.parse(localStorage.getItem(this.storageKey)) || [])))
    this.isWishlistPage = MinimogSettings.template === this.pageTemplate
    this.init()
  }

  init = async () => {
    if (this.isWishlistPage) {
      await this.renderWishlistPage()
      this.addEventToRemoveButtons()
    }
    this.setWishlistButtonsState()
    this.addEventToWishlistButtons()
    this.updateWishlistCount()
  }

  saveToStorage = () => {
    this.products = Array.from(new Set(this.products))
    localStorage.setItem(this.storageKey, JSON.stringify(this.products))
  }

  addToWishlist(handle) {
    if (handle && this.products.indexOf(handle) === -1) {
      this.products.push(handle)
      this.saveToStorage()
    }
  }

  removeFromWishlist(handle) {
    this.products = this.products.filter(hdl => hdl !== handle)
    this.saveToStorage()
  }

  setWishlistButtonsState = () => {
    const buttons = document.querySelectorAll(this.selectors.wishlistButton)
    buttons.forEach(btn => {
      const prodHandle = btn && btn.dataset.productHandle
      if (this.products.indexOf(prodHandle) >= 0 && btn && !btn.classList.contains(this.addedClass)) {
        this.toggleButtonState(btn, true)

        if (this.isWishlistPage) {
          btn.classList.remove(this.selectors.wishlistButton.replace('.', ''))
          btn.classList.add(this.selectors.removeButton.replace('.', ''))
        }
      }
    })
  }

  updateWishlistCount = () => {
    const size = this.products.length
    const countElems = document.querySelectorAll(this.selectors.count);
    [...countElems].forEach(elem => {
      elem.textContent = size
      if (size < 1) {
        elem.classList.add('m:hidden')
      } else {
        elem.classList.remove('m:hidden')
      }
    })
    const method = size ? 'add' : 'remove'
    document.body.classList[method](this.hasItemClass)
  }

  addEventToWishlistButtons = () => {
    addEventDelegate({
      selector: this.selectors.wishlistButton,
      handler: (e, btn) => {
        e.preventDefault()
        const productHandle = btn && btn.dataset.productHandle
        if (productHandle) {
          const active = !btn.classList.contains(this.addedClass)
          this.toggleButtonState(btn, active)
          this.updateWishlistCount()

          document.querySelectorAll(this.selectors.wishlistButton).forEach(btnItem => {
            if (btnItem && btnItem.dataset.productHandle === productHandle && btnItem !== btn) {
              const isAdded = !btnItem.classList.contains(this.addedClass)
              this.toggleButtonState(btnItem, isAdded)
            }
          })
        }
      }
    })
  }

  toggleButtonState = (btn, active) => {
    const productHandle = btn && btn.dataset.productHandle
    const wishlistText = btn && btn.querySelector(this.selectors.wishlistText)

    if (active) {
      this.addToWishlist(productHandle)
      btn.classList.add(this.addedClass)
    } else {
      this.removeFromWishlist(productHandle)
      btn.classList.remove(this.addedClass)
    }

    if (wishlistText) {
      const temp = wishlistText.dataset.revertText
      wishlistText.dataset.revertText = wishlistText.textContent
      wishlistText.textContent = temp
    }
  }

  addEventToRemoveButtons = () => {
    addEventDelegate({
      selector: this.selectors.removeButton,
      handler: (e, btn) => {
        e.preventDefault()
        const prod = btn && btn.closest(this.selectors.wrapper)
        prod && prod.remove()

        const productHandle = btn && btn.dataset.productHandle
        if (productHandle) {
          this.removeFromWishlist(productHandle)
          this.updateWishlistCount()
          if (!this.products.length) {
            this.showNoProductsMessage()
          }
        }
      }
    })
  }

  wishlistRemoveButton(prdHandle) {
    const html = `<svg class="m-svg-icon--medium" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>`;
    const button = document.createElement("DIV");
    button.classList.add("m-tooltip", "m-wishlist-remove-button", "m:block", "md:m:hidden");
    button.setAttribute("data-product-handle", prdHandle);
    button.innerHTML = html;
    return button;
  }

  fetchProductCard = async (handle) => {
    const url = formatUrl('products', handle, 'view=grid-card-item')
    try {
      // Status-aware fetch (unlike fetchCache, which resolves the full 404 page
      // as text). A removed/unpublished product 404s; a deleted product that has
      // a URL redirect resolves elsewhere. Both mean "no longer available".
      const res = await fetch(url, { credentials: 'same-origin' })
      if (!res.ok || res.redirected) return null
      return await res.text()
    } catch (e) {
      return null
    }
  }

  renderWishlistPage = async () => {
    const container = document.querySelector(this.selectors.container)
    if (!container) return

    // Any handle that no longer resolves to a valid product card (deleted /
    // unpublished / unavailable) is collected here so it can be pruned. Using
    // allSettled + per-item guards guarantees a single bad handle can never
    // reject the batch and leave the page stuck on the loading spinner.
    const unavailableHandles = []

    try {
      let noItemAvailable = true

      if (this.products.length) {
        const results = await Promise.allSettled(
          this.products.map(async hdl => {
            const prodHTML = await this.fetchProductCard(hdl)
            if (!prodHTML) return false

            const item = document.createElement("DIV");
            item.classList.add("m:hidden", "m:column", "m-wishlist-card");
            item.innerHTML = prodHTML

            // A valid response is the isolated product-card fragment. If the
            // markup carries the site-wide loading overlay it is really a full
            // page (404 / redirect); injecting that overlay is what left the
            // page spinning forever, so treat it as unavailable.
            const card = item.querySelector(this.selectors.productCard)
            if (!card || item.querySelector('.m-page-transition')) return false

            item.appendChild(this.wishlistRemoveButton(hdl));
            this.productNodes[hdl] = item
            return true
          })
        )

        this.products.forEach((hdl, index) => {
          const result = results[index]
          const rendered = result && result.status === 'fulfilled' && result.value === true
          if (!rendered) unavailableHandles.push(hdl)
        })

        // Render available items in original order
        this.products.forEach(hdl => {
          const prod = this.productNodes[hdl]
          if (prod) {
            noItemAvailable = false
            container.appendChild(prod)

            if (MinimogTheme.CompareProduct) {
              MinimogTheme.CompareProduct.setCompareButtonsState();
            }

            prod.classList.remove('m:hidden');
          }
        })
      }

      // Drop unavailable items from storage (+ account metafield) and tell the
      // customer why they disappeared.
      if (unavailableHandles.length) {
        this.removeUnavailableProducts(unavailableHandles)
      }

      if (noItemAvailable) {
        this.showNoProductsMessage()
      } else {
        this.setWishlistButtonsState()
      }
    } catch (err) {
      console.error('[wishlist] Failed to render wishlist page:', err)
    } finally {
      // Always reveal the container so the page can never hang on loading.
      container.classList.add('is-visible')
    }
  }

  removeUnavailableProducts = (handles) => {
    if (!handles || !handles.length) return

    const removeSet = new Set(handles)
    this.products = this.products.filter(hdl => !removeSet.has(hdl))

    // Tombstone the removed handles up-front so a not-yet-synced metafield can't
    // resurrect them on a quick reload; saveToStorage() then triggers the
    // account back-sync (patched by wishlist-account-sync.js).
    if (window.__wlSync && typeof window.__wlSync.addTombstones === 'function') {
      window.__wlSync.addTombstones(handles)
    }
    this.saveToStorage()
    this.updateWishlistCount()
    this.showUnavailableNotice(handles.length)
  }

  showUnavailableNotice = (count) => {
    const notice = document.querySelector(this.selectors.unavailableNotice)
    if (!notice) return
    const countElem = notice.querySelector('[data-unavailable-count]')
    if (countElem) countElem.textContent = count
    notice.classList.remove('m:hidden')
  }

  showNoProductsMessage = () => {
    const container = document.querySelector(this.selectors.container)
    const noProducts = document.querySelector(this.selectors.noProducts)

    container.classList.add('m:hidden')
    noProducts.classList.remove('m:hidden')
  }
}

MinimogTheme.Wishlist = new Wishlist()